
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { 
  canUserSendChatMessage, 
  canUserGeneratePrompt, 
  canUserSendCopilotMessage,
  hasProductCopilotAccess,
  getUserSubscription 
} from "@/services/subscriptionService";

export interface AIUsageData {
  chatMessages: {
    canSend: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  };
  promptGenerations: {
    canGenerate: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  };
  copilotMessages: {
    canSend: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  };
  productCopilot: {
    hasAccess: boolean;
  };
  subscriptionTier: string;
}

export const useAIUsage = () => {
  const { user } = useAuth();
  const [aiUsage, setAIUsage] = useState<AIUsageData>({
    chatMessages: {
      canSend: true,
      usedCount: 0,
      limit: 50,
      percentUsed: 0
    },
    promptGenerations: {
      canGenerate: true,
      usedCount: 0,
      limit: 10,
      percentUsed: 0
    },
    copilotMessages: {
      canSend: true,
      usedCount: 0,
      limit: 5, // Free tier gets 5 Product Copilot credits
      percentUsed: 0
    },
    productCopilot: {
      hasAccess: true // Everyone has access, but limited by credits
    },
    subscriptionTier: 'free'
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadAIUsage();
    } else {
      // Set default limits for non-authenticated users
      setAIUsage({
        chatMessages: {
          canSend: false,
          usedCount: 0,
          limit: 0,
          percentUsed: 0
        },
        promptGenerations: {
          canGenerate: false,
          usedCount: 0,
          limit: 0,
          percentUsed: 0
        },
        copilotMessages: {
          canSend: false,
          usedCount: 0,
          limit: 0,
          percentUsed: 0
        },
        productCopilot: {
          hasAccess: false
        },
        subscriptionTier: 'free'
      });
      setIsLoading(false);
    }
  }, [user?.id]);

  const loadAIUsage = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      const [chatData, generationData, copilotData, copilotAccess, subscription] = await Promise.all([
        canUserSendChatMessage(user.id),
        canUserGeneratePrompt(user.id),
        canUserSendCopilotMessage(user.id),
        hasProductCopilotAccess(user.id),
        getUserSubscription(user.id)
      ]);

      // Determine subscription tier
      let subscriptionTier = 'free';
      if (subscription?.tier?.name) {
        const tierName = subscription.tier.name.toLowerCase();
        if (tierName.includes('plus')) {
          subscriptionTier = 'plus';
        } else if (tierName.includes('pro')) {
          subscriptionTier = 'pro';
        }
      }

      setAIUsage({
        chatMessages: chatData,
        promptGenerations: generationData,
        copilotMessages: copilotData,
        productCopilot: {
          hasAccess: true // Everyone has access, limited by credits
        },
        subscriptionTier
      });
    } catch (error) {
      console.error("Error loading AI usage:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const refetch = () => {
    if (user?.id) {
      loadAIUsage();
    }
  };

  return {
    data: aiUsage,
    isLoading,
    refetch
  };
};
